import React, { Component } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Cell, ComposedChart, Line } from 'recharts';
import { Download, ArrowRight, TrendingDown, Shield } from 'lucide-react';
// --- Mock Data ---
const lossFlowData = [{
  name: 'Gross Losses',
  value: 12500000,
  fill: '#3B82F6'
}, {
  name: 'Ceded to Reinsurers',
  value: -4200000,
  fill: '#10B981'
}, {
  name: 'Net Retained',
  value: 8300000,
  fill: '#6366F1'
}];
const retroRecoveriesData = [{
  quarter: 'Q1 2024',
  paid: 850,
  outstanding: 320
}, {
  quarter: 'Q2 2024',
  paid: 920,
  outstanding: 280
}, {
  quarter: 'Q3 2024',
  paid: 1050,
  outstanding: 410
}, {
  quarter: 'Q4 2024',
  paid: 1180,
  outstanding: 390
}];
const recoverablesAgingData = [{
  bucket: '0-30 days',
  amount: 1250,
  percentage: 45
}, {
  bucket: '31-60 days',
  amount: 820,
  percentage: 30
}, {
  bucket: '61-90 days',
  amount: 420,
  percentage: 15
}, {
  bucket: '91-180 days',
  amount: 210,
  percentage: 8
}, {
  bucket: '180+ days',
  amount: 80,
  percentage: 2
}];
const reinsurerExposureData = [{
  rank: 1,
  reinsurer: 'Munich Re',
  rating: 'AA',
  exposure: 4500000,
  percentage: 28.5,
  recoverables: 1250000,
  status: 'Active'
}, {
  rank: 2,
  reinsurer: 'Swiss Re',
  rating: 'AA-',
  exposure: 3800000,
  percentage: 24.1,
  recoverables: 980000,
  status: 'Active'
}, {
  rank: 3,
  reinsurer: 'Hannover Re',
  rating: 'AA-',
  exposure: 2900000,
  percentage: 18.4,
  recoverables: 720000,
  status: 'Active'
}, {
  rank: 4,
  reinsurer: 'SCOR',
  rating: 'A+',
  exposure: 2100000,
  percentage: 13.3,
  recoverables: 540000,
  status: 'Active'
}, {
  rank: 5,
  reinsurer: 'Gen Re',
  rating: 'AA',
  exposure: 1500000,
  percentage: 9.5,
  recoverables: 380000,
  status: 'Active'
}, {
  rank: 6,
  reinsurer: 'RenaissanceRe',
  rating: 'A+',
  exposure: 980000,
  percentage: 6.2,
  recoverables: 210000,
  status: 'Active'
}];
// --- Colors ---
const COLORS = {
  gross: '#3B82F6',
  ceded: '#10B981',
  net: '#6366F1',
  paid: '#10B981',
  outstanding: '#F59E0B',
  aging: ['#3B82F6', '#60A5FA', '#93C5FD', '#BFDBFE', '#DBEAFE']
};
// --- Components ---
const CustomTooltip = ({
  active,
  payload,
  label,
  unit = '£K'
}: any) => {
  if (active && payload && payload.length) {
    return <div className="bg-white p-3 border border-gray-200 shadow-lg rounded-md text-sm" data-id="element-1982">
        <p className="font-semibold text-gray-900 mb-2" data-id="element-1983">{label}</p>
        {payload.map((entry: any, index: number) => <div key={index} className="flex items-center gap-2 mb-1 last:mb-0" data-id="element-1984">
            <div className="w-3 h-3 rounded-full" style={{
          backgroundColor: entry.color || entry.fill
        }} data-id="element-1985" />
            <span className="text-gray-600" data-id="element-1986">{entry.name}:</span>
            <span className="font-medium text-gray-900" data-id="element-1987">
              {unit === '£' ? '£' : ''}
              {Math.abs(entry.value).toLocaleString()}
              {unit === '£K' ? 'K' : unit === '%' ? '%' : ''}
            </span>
          </div>)}
      </div>;
  }
  return null;
};
const FlowDiagram = () => {
  return <div className="flex items-center justify-between py-8" data-id="element-1988">
      {/* Gross Losses */}
      <div className="flex flex-col items-center" data-id="element-1989">
        <div className="w-32 h-32 rounded-full bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center text-white shadow-lg" data-id="element-1990">
          <div className="text-center" data-id="element-1991">
            <div className="text-2xl font-bold" data-id="element-1992">£12.5M</div>
            <div className="text-xs mt-1 opacity-90" data-id="element-1993">Gross</div>
          </div>
        </div>
        <p className="mt-3 text-sm font-medium text-gray-700" data-id="element-1994">Gross Losses</p>
      </div>

      {/* Arrow + Ceded Amount */}
      <div className="flex flex-col items-center flex-1 mx-4" data-id="element-1995">
        <div className="flex items-center w-full" data-id="element-1996">
          <div className="flex-1 h-0.5 bg-gradient-to-r from-blue-400 to-green-400" data-id="element-1997"></div>
          <ArrowRight className="text-green-500 mx-2" size={24} data-id="element-1998" />
          <div className="flex-1 h-0.5 bg-gradient-to-r from-green-400 to-indigo-400" data-id="element-1999"></div>
        </div>
        <div className="mt-2 px-4 py-2 bg-green-50 rounded-full border border-green-200" data-id="element-2000">
          <div className="flex items-center gap-2" data-id="element-2001">
            <TrendingDown className="text-green-600" size={16} data-id="element-2002" />
            <span className="text-sm font-semibold text-green-700" data-id="element-2003">
              £4.2M Ceded
            </span>
          </div>
        </div>
        <p className="mt-2 text-xs text-gray-500" data-id="element-2004">Transferred to Reinsurers</p>
      </div>

      {/* Net Retained */}
      <div className="flex flex-col items-center" data-id="element-2005">
        <div className="w-32 h-32 rounded-full bg-gradient-to-br from-indigo-500 to-indigo-600 flex items-center justify-center text-white shadow-lg" data-id="element-2006">
          <div className="text-center" data-id="element-2007">
            <div className="text-2xl font-bold" data-id="element-2008">£8.3M</div>
            <div className="text-xs mt-1 opacity-90" data-id="element-2009">Net</div>
          </div>
        </div>
        <p className="mt-3 text-sm font-medium text-gray-700" data-id="element-2010">Net Retained</p>
      </div>
    </div>;
};
const getRatingColor = (rating: string) => {
  if (rating.startsWith('AA')) return 'bg-green-100 text-green-800';
  if (rating.startsWith('A')) return 'bg-blue-100 text-blue-800';
  return 'bg-gray-100 text-gray-800';
};
export function ReinsuranceRetrocession() {
  return <div className="space-y-6 animate-in fade-in duration-500" data-id="element-2011">
      {/* Header */}
      <div className="flex justify-between items-center" data-id="element-2012">
        <div data-id="element-2013">
          <h1 className="text-2xl font-bold text-gray-900" data-id="element-2014">
            Reinsurance & Retrocession
          </h1>
          <p className="text-gray-500 mt-1" data-id="element-2015">
            Risk transfer effectiveness and recoverability analysis • FY 2024
          </p>
        </div>
        <div className="flex items-center gap-4" data-id="element-2016">
          <div className="flex items-center gap-2 px-3 py-1 bg-blue-50 rounded-full" data-id="element-2017">
            <Shield className="text-blue-600" size={16} data-id="element-2018" />
            <span className="text-sm font-medium text-blue-700" data-id="element-2019">
              66% Risk Transferred
            </span>
          </div>
          <button className="flex items-center px-4 py-2 bg-white border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors shadow-sm" data-id="element-2020">
            <Download size={16} className="mr-2" data-id="element-2021" />
            Export Report
          </button>
        </div>
      </div>

      {/* Top Row: Loss Flow Diagram */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6" data-id="element-2022">
        <h2 className="text-lg font-semibold text-gray-900 mb-2" data-id="element-2023">
          Loss Flow Analysis
        </h2>
        <p className="text-sm text-gray-500 mb-6" data-id="element-2024">
          Gross losses to net retained position
        </p>
        <FlowDiagram data-id="element-2025" />
        <div className="mt-6 pt-6 border-t border-gray-100 grid grid-cols-3 gap-4 text-center" data-id="element-2026">
          <div data-id="element-2027">
            <p className="text-xs text-gray-500 uppercase tracking-wide" data-id="element-2028">
              Cession Rate
            </p>
            <p className="text-2xl font-bold text-gray-900 mt-1" data-id="element-2029">33.6%</p>
          </div>
          <div className="border-l border-gray-100" data-id="element-2030">
            <p className="text-xs text-gray-500 uppercase tracking-wide" data-id="element-2031">
              Net Retention
            </p>
            <p className="text-2xl font-bold text-gray-900 mt-1" data-id="element-2032">66.4%</p>
          </div>
          <div className="border-l border-gray-100" data-id="element-2033">
            <p className="text-xs text-gray-500 uppercase tracking-wide" data-id="element-2034">
              Risk Transfer Efficiency
            </p>
            <p className="text-2xl font-bold text-green-600 mt-1" data-id="element-2035">Optimal</p>
          </div>
        </div>
      </div>

      {/* Middle Row: Two Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-id="element-2036">
        {/* Retro Recoveries */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6" data-id="element-2037">
          <h2 className="text-lg font-semibold text-gray-900 mb-6" data-id="element-2038">
            Retro Recoveries
          </h2>
          <div className="h-64" data-id="element-2039">
            <ResponsiveContainer width="100%" height="100%" data-id="element-2040">
              <BarChart data={retroRecoveriesData} margin={{
              top: 5,
              right: 30,
              left: 20,
              bottom: 5
            }} data-id="element-2041">
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E5E7EB" data-id="element-2042" />
                <XAxis dataKey="quarter" axisLine={false} tickLine={false} tick={{
                fill: '#6B7280',
                fontSize: 12
              }} dy={10} data-id="element-2043" />
                <YAxis axisLine={false} tickLine={false} tick={{
                fill: '#6B7280',
                fontSize: 12
              }} tickFormatter={value => `£${value}K`} data-id="element-2044" />
                <Tooltip content={<CustomTooltip unit="£K" data-id="element-2046" />} data-id="element-2045" />
                <Legend verticalAlign="top" align="right" wrapperStyle={{
                paddingBottom: '20px'
              }} iconType="circle" data-id="element-2047" />
                <Bar dataKey="paid" name="Paid Recoveries" fill={COLORS.paid} radius={[4, 4, 0, 0]} data-id="element-2048" />
                <Bar dataKey="outstanding" name="Outstanding" fill={COLORS.outstanding} radius={[4, 4, 0, 0]} data-id="element-2049" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Recoverables Aging */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6" data-id="element-2050">
          <h2 className="text-lg font-semibold text-gray-900 mb-6" data-id="element-2051">
            Recoverables Aging
          </h2>
          <div className="h-64" data-id="element-2052">
            <ResponsiveContainer width="100%" height="100%" data-id="element-2053">
              <BarChart data={recoverablesAgingData} margin={{
              top: 5,
              right: 30,
              left: 20,
              bottom: 5
            }} data-id="element-2054">
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E5E7EB" data-id="element-2055" />
                <XAxis dataKey="bucket" axisLine={false} tickLine={false} tick={{
                fill: '#6B7280',
                fontSize: 11
              }} dy={10} angle={-15} textAnchor="end" data-id="element-2056" />
                <YAxis axisLine={false} tickLine={false} tick={{
                fill: '#6B7280',
                fontSize: 12
              }} tickFormatter={value => `£${value}K`} data-id="element-2057" />
                <Tooltip content={<CustomTooltip unit="£K" data-id="element-2059" />} data-id="element-2058" />
                <Bar dataKey="amount" name="Amount" radius={[4, 4, 0, 0]} data-id="element-2060">
                  {recoverablesAgingData.map((entry, index) => <Cell key={`cell-${index}`} fill={COLORS.aging[index]} data-id="element-2061" />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
          <p className="mt-4 text-sm text-gray-500" data-id="element-2062">
            <span className="font-semibold text-green-600" data-id="element-2063">75%</span> of
            recoverables are current (0-60 days)
          </p>
        </div>
      </div>

      {/* Bottom Row: Reinsurer Exposure Table */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6" data-id="element-2064">
        <div className="flex justify-between items-center mb-6" data-id="element-2065">
          <h2 className="text-lg font-semibold text-gray-900" data-id="element-2066">
            Reinsurer Exposure by Counterparty
          </h2>
          <div className="text-sm text-gray-500" data-id="element-2067">
            Total Exposure:{' '}
            <span className="font-semibold text-gray-900" data-id="element-2068">£15.8M</span>
          </div>
        </div>

        <div className="overflow-x-auto" data-id="element-2069">
          <table className="w-full" data-id="element-2070">
            <thead data-id="element-2071">
              <tr className="border-b border-gray-200" data-id="element-2072">
                <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider" data-id="element-2073">
                  Rank
                </th>
                <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider" data-id="element-2074">
                  Reinsurer
                </th>
                <th className="text-center py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider" data-id="element-2075">
                  Rating
                </th>
                <th className="text-right py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider" data-id="element-2076">
                  Exposure
                </th>
                <th className="text-right py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider" data-id="element-2077">
                  % of Total
                </th>
                <th className="text-right py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider" data-id="element-2078">
                  Recoverables
                </th>
                <th className="text-center py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider" data-id="element-2079">
                  Status
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100" data-id="element-2080">
              {reinsurerExposureData.map(reinsurer => <tr key={reinsurer.rank} className="hover:bg-gray-50 transition-colors" data-id="element-2081">
                  <td className="py-3 px-4 text-sm text-gray-500" data-id="element-2082">
                    {reinsurer.rank}
                  </td>
                  <td className="py-3 px-4 text-sm font-medium text-gray-900" data-id="element-2083">
                    {reinsurer.reinsurer}
                  </td>
                  <td className="py-3 px-4 text-center" data-id="element-2084">
                    <span className={`inline-flex px-2 py-1 text-xs font-medium rounded ${getRatingColor(reinsurer.rating)}`} data-id="element-2085">
                      {reinsurer.rating}
                    </span>
                  </td>
                  <td className="py-3 px-4 text-sm text-right font-semibold text-gray-900" data-id="element-2086">
                    £{(reinsurer.exposure / 1000000).toFixed(1)}M
                  </td>
                  <td className="py-3 px-4 text-sm text-right text-gray-600" data-id="element-2087">
                    {reinsurer.percentage.toFixed(1)}%
                  </td>
                  <td className="py-3 px-4 text-sm text-right text-gray-700" data-id="element-2088">
                    £{(reinsurer.recoverables / 1000000).toFixed(2)}M
                  </td>
                  <td className="py-3 px-4 text-center" data-id="element-2089">
                    <span className="inline-flex px-2 py-1 text-xs font-medium rounded bg-green-100 text-green-800" data-id="element-2090">
                      {reinsurer.status}
                    </span>
                  </td>
                </tr>)}
            </tbody>
            <tfoot className="border-t-2 border-gray-300" data-id="element-2091">
              <tr className="bg-gray-50" data-id="element-2092">
                <td colSpan={3} className="py-3 px-4 text-sm font-bold text-gray-900" data-id="element-2093">
                  Total
                </td>
                <td className="py-3 px-4 text-sm text-right font-bold text-gray-900" data-id="element-2094">
                  £
                  {(reinsurerExposureData.reduce((sum, r) => sum + r.exposure, 0) / 1000000).toFixed(1)}
                  M
                </td>
                <td className="py-3 px-4 text-sm text-right font-bold text-gray-900" data-id="element-2095">
                  100.0%
                </td>
                <td className="py-3 px-4 text-sm text-right font-bold text-gray-900" data-id="element-2096">
                  £
                  {(reinsurerExposureData.reduce((sum, r) => sum + r.recoverables, 0) / 1000000).toFixed(2)}
                  M
                </td>
                <td data-id="element-2097"></td>
              </tr>
            </tfoot>
          </table>
        </div>
        <p className="mt-4 text-sm text-gray-500 italic" data-id="element-2098">
          * All reinsurers maintain investment-grade ratings (A+ or higher).
          Concentration risk is well-managed with no single counterparty
          exceeding 30% of total exposure.
        </p>
      </div>
    </div>;
}